/**
 * useAvailableTypes.ts
 *
 * Hook for fetching schema types available for creation within a sphere,
 * based on approaches deployed to the sphere and its ancestors, plus system schemas.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import type { ApiClients } from '../context/ApiClientsContext';
import type { AppDispatch } from '@center-pwa/store/store';
import type { Schema } from 'types/schema';
import { warmCache } from '@center-pwa/reducers/schemaTypesReducer';
import { globalSphereCache } from '@center-platform/spheres/cache';

/** Template info returned when includeTemplates is requested */
export interface TemplateInfo {
  approachSystemName: string;
  approachName: string | Record<string, string>;
}

/** Data returned by GET /spheres/:sphereId/available-types */
export interface AvailableTypesData {
  schemas: Schema[];
  approachSources: Record<string, string[]>;
  templates?: Record<string, TemplateInfo[]>;
}

/** Options for the useAvailableTypes hook */
export interface UseAvailableTypesOptions {
  includeTemplates?: boolean;
}

/** Return value of the useAvailableTypes hook */
export interface UseAvailableTypesReturn {
  schemas: Schema[];
  approachSources: Record<string, string[]>;
  templates: Record<string, TemplateInfo[]>;
  /**
   * node_label of the sphere itself (its Gremlin vertex label), resolved via
   * globalSphereCache. Eligibility filtering needs the assignee's node_label, and
   * the sphere's own type is deliberately NOT in `schemas`; this exposes it
   * org-independently (unlike useSchemaType, which needs an organizationId and a
   * fetchable schema). Undefined until resolved / when no sphereId is given.
   */
  assigneeNodeLabel: string | undefined;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches available schema types for a sphere based on deployed approaches.
 *
 * @param apiClients - API clients (pass null if not ready)
 * @param sphereId - Sphere to fetch available types for (null/undefined skips fetch)
 * @param options - Optional settings (e.g. includeTemplates)
 */
export function useAvailableTypes(
  apiClients: ApiClients | null,
  sphereId: string | null | undefined,
  options?: UseAvailableTypesOptions
): UseAvailableTypesReturn {
  const dispatch = useDispatch<AppDispatch>();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [approachSources, setApproachSources] = useState<Record<string, string[]>>({});
  const [templates, setTemplates] = useState<Record<string, TemplateInfo[]>>({});
  const [assigneeNodeLabel, setAssigneeNodeLabel] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const includeTemplates = options?.includeTemplates ?? false;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchAvailableTypes = useCallback(
    async (skipCache = false) => {
      if (!apiClients?.graphClient || !sphereId) return;

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, string> = {};
        if (skipCache) {
          params.skipCache = 'true';
        }
        if (includeTemplates) {
          params.includeTemplates = 'true';
        }
        const data = await apiClients.graphClient.get<AvailableTypesData>(
          `/spheres/${sphereId}/available-types`,
          { params }
        );

        if (isMountedRef.current) {
          setSchemas(data.schemas);
          setApproachSources(data.approachSources);
          setTemplates(data.templates ?? {});
          // Warm the schemaTypes demand cache so point-lookup consumers get
          // cache hits after a picker visit without an additional round-trip.
          if (organizationId && data.schemas.length > 0) {
            dispatch(warmCache({ schemas: data.schemas, organizationId, fetchedAt: Date.now() }));
          }
          // Resolve the sphere's own node_label (its vertex label) for eligibility
          // matching. Best-effort and org-independent via globalSphereCache; a
          // failure just leaves assigneeNodeLabel undefined.
          globalSphereCache
            .getSphereData(sphereId, apiClients.graphClient)
            .then((sphereData) => {
              if (isMountedRef.current) {
                setAssigneeNodeLabel(sphereData?.info?.sphere?.label);
              }
            })
            .catch(() => {
              /* node_label is best-effort; eligibility falls back to other sources */
            });
        }
      } catch (err: unknown) {
        if (isMountedRef.current) {
          setError(
            err instanceof Error ? err.message : 'Failed to fetch available types'
          );
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [apiClients, sphereId, includeTemplates, organizationId, dispatch]
  );

  // Fetch on mount and when sphereId changes
  useEffect(() => {
    if (!apiClients || !sphereId) {
      setSchemas([]);
      setApproachSources({});
      setTemplates({});
      setAssigneeNodeLabel(undefined);
      return;
    }

    fetchAvailableTypes();
  }, [apiClients, sphereId, fetchAvailableTypes]);

  const refetch = useCallback(() => {
    fetchAvailableTypes(true);
  }, [fetchAvailableTypes]);

  return { schemas, approachSources, templates, assigneeNodeLabel, loading, error, refetch };
}
